/**
 * Project Selector Component
 *
 * Dropdown component for selecting the current project context.
 * Similar to Claude Code's repository selection.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  FolderOpen as FolderIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Check as CheckIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Lock as LockIcon,
  ChatBubbleOutline as ChatIcon,
} from '@mui/icons-material';
import {
  Project,
  ProjectCreate,
  listProjects,
  createProject,
  setCurrentProject,
  getCurrentProject,
  clearCurrentProject,
  saveCurrentProjectLocally,
  getLocalCurrentProject,
  saveDefaultProject,
  getDefaultProject,
  isDefaultProject,
} from '../services/project.service';

interface ProjectSelectorProps {
  onProjectChange?: (project: Project | null) => void;
  onSettingsClick?: () => void;
  /** When true, the selector is locked and the user cannot switch projects. */
  locked?: boolean;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  onProjectChange,
  onSettingsClick,
  locked = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProjectState] = useState<Project | null>(null);
  const [defaultProject, setDefaultProjectState] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New project dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState<ProjectCreate>({
    name: '',
    key: '',
    description: '',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const open = Boolean(anchorEl);

  // Track whether we've resolved the initial project
  const [initialResolved, setInitialResolved] = useState(false);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
    loadCurrentProject();
    loadDefaultProject();
  }, []);

  // Validate current project still exists in the loaded project list
  useEffect(() => {
    if (loading || !initialResolved || projects.length === 0) return;

    if (currentProject) {
      // Check if the stored project still exists in the actual project list
      const found = projects.find((p) => p.id === currentProject.id);
      if (found) {
        // /projects/current/info only carries id/name/key — swap in the full
        // record from the list so the header and settings see everything.
        if (found !== currentProject && (currentProject.created_at === '' || !currentProject.key)) {
          setCurrentProjectState(found);
          saveCurrentProjectLocally(found);
          onProjectChange?.(found);
        }
        return; // Current project is valid
      }
      // Stored project no longer exists — clear it
      setCurrentProjectState(null);
      saveCurrentProjectLocally(null);
      onProjectChange?.(null);
    }
    // If no project selected, that's fine — user can chat without a project
  }, [loading, initialResolved, currentProject, projects]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await listProjects();
      setProjects(response.projects);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultProject = () => {
    const defaultProj = getDefaultProject();
    setDefaultProjectState(defaultProj);
  };

  // A project restored from this browser (last selection or default) is
  // pushed to the server so the header and the agent agree on the current
  // project — the server copy is what chat requests fall back to.
  const adoptLocalProject = (project: Project) => {
    setCurrentProjectState(project);
    saveCurrentProjectLocally(project);
    onProjectChange?.(project);
    setCurrentProject(project.id).catch(() => {
      // Server unreachable or project no longer allowed — keep the local
      // choice for this tab; the list validation below drops it if invalid.
    });
  };

  const loadCurrentProject = async () => {
    try {
      // The server's current project is authoritative: it is what the agent
      // actually runs against when a chat request names no project.
      const project = await getCurrentProject();
      if (project) {
        setCurrentProjectState(project);
        saveCurrentProjectLocally(project);
        onProjectChange?.(project);
      } else {
        // Fall back to local storage, then the default project
        const localProject = getLocalCurrentProject() || getDefaultProject();
        if (localProject) {
          adoptLocalProject(localProject);
        }
      }
    } catch (err) {
      // Fall back to local storage, then the default project, on error
      const localProject = getLocalCurrentProject() || getDefaultProject();
      if (localProject) {
        adoptLocalProject(localProject);
      }
    } finally {
      setInitialResolved(true);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectProject = async (project: Project) => {
    try {
      await setCurrentProject(project.id);
      setCurrentProjectState(project);
      saveCurrentProjectLocally(project);
      onProjectChange?.(project);
      handleClose();
    } catch (err) {
      // Still set locally even if server fails
      setCurrentProjectState(project);
      saveCurrentProjectLocally(project);
      onProjectChange?.(project);
      handleClose();
    }
  };

  const handleClearProject = () => {
    setCurrentProjectState(null);
    saveCurrentProjectLocally(null);
    onProjectChange?.(null);
    handleClose();
    // Clear on the server too, otherwise chat would silently fall back to the
    // project the server still has stored.
    clearCurrentProject().catch(() => {});
  };

  const handleSetAsDefault = (project: Project, event: React.MouseEvent) => {
    event.stopPropagation();
    if (defaultProject?.id === project.id) {
      // Toggle off if already default
      saveDefaultProject(null);
      setDefaultProjectState(null);
    } else {
      saveDefaultProject(project);
      setDefaultProjectState(project);
    }
  };

  const handleSetCurrentAsDefault = () => {
    if (currentProject) {
      if (defaultProject?.id === currentProject.id) {
        saveDefaultProject(null);
        setDefaultProjectState(null);
      } else {
        saveDefaultProject(currentProject);
        setDefaultProjectState(currentProject);
      }
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setNewProject({ name: '', key: '', description: '' });
    setCreateError(null);
    handleClose();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleCreateProject = async () => {
    if (!newProject.name || !newProject.key) {
      setCreateError('Name and Key are required');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      const created = await createProject(newProject);
      setProjects([...projects, created]);
      handleSelectProject(created);
      handleCloseDialog();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleKeyChange = (value: string) => {
    // Convert to uppercase and remove invalid characters
    const key = value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    setNewProject({ ...newProject, key });
  };

  const isCurrentDefault = currentProject && defaultProject?.id === currentProject.id;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={locked ? 'Project is locked for this conversation. Start a new chat to switch.' : ''}>
          <span>
            <Button
              onClick={locked ? undefined : handleClick}
              variant="outlined"
              color="inherit"
              size="small"
              disabled={locked}
              endIcon={locked ? <LockIcon fontSize="small" /> : <ArrowDownIcon />}
              startIcon={<FolderIcon />}
              sx={{
                borderColor: 'divider',
                color: locked ? 'text.secondary' : 'text.primary',
                textTransform: 'none',
                minWidth: 150,
                justifyContent: 'flex-start',
                '&.Mui-disabled': {
                  borderColor: 'divider',
                  color: 'text.secondary',
                  opacity: 0.8,
                },
              }}
            >
              {loading ? (
                <CircularProgress size={16} />
              ) : currentProject ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={currentProject.key}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                    {currentProject.name}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No project
                </Typography>
              )}
            </Button>
          </span>
        </Tooltip>

        {/* Default Button - similar to Claude Code */}
        {currentProject && !locked && (
          <Tooltip title={isCurrentDefault ? "This is your default project" : "Set as default project"}>
            <Button
              onClick={handleSetCurrentAsDefault}
              variant={isCurrentDefault ? "contained" : "outlined"}
              color={isCurrentDefault ? "primary" : "inherit"}
              size="small"
              startIcon={isCurrentDefault ? <StarIcon /> : <StarBorderIcon />}
              sx={{
                textTransform: 'none',
                borderColor: isCurrentDefault ? undefined : 'divider',
                minWidth: 90,
                '&:hover': {
                  borderColor: isCurrentDefault ? undefined : 'primary.main',
                },
              }}
            >
              Default
            </Button>
          </Tooltip>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxHeight: 400,
          },
        }}
      >
        {error && (
          <MenuItem disabled>
            <Alert severity="error" sx={{ width: '100%', py: 0 }}>
              {error}
            </Alert>
          </MenuItem>
        )}

        {/* Option to chat without selecting a project */}
        <MenuItem
          onClick={handleClearProject}
          selected={!currentProject}
        >
          <ListItemIcon>
            {!currentProject ? (
              <CheckIcon color="primary" fontSize="small" />
            ) : (
              <ChatIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2">No project</Typography>
            }
            secondary={
              <Typography variant="caption" color="text.secondary">
                Chat freely — describe your app to auto-create one
              </Typography>
            }
          />
        </MenuItem>

        {projects.length > 0 && <Divider sx={{ my: 1 }} />}

        {projects.length === 0 && !loading && !error && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No projects available
            </Typography>
          </MenuItem>
        )}

        {projects.map((project) => {
          const isProjectDefault = defaultProject?.id === project.id;
          return (
            <MenuItem
              key={project.id}
              onClick={() => handleSelectProject(project)}
              selected={currentProject?.id === project.id}
            >
              <ListItemIcon>
                {currentProject?.id === project.id ? (
                  <CheckIcon color="primary" fontSize="small" />
                ) : (
                  <FolderIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={project.key}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    <Typography variant="body2">{project.name}</Typography>
                    {isProjectDefault && (
                      <Chip
                        icon={<StarIcon sx={{ fontSize: 14 }} />}
                        label="Default"
                        size="small"
                        color="primary"
                        sx={{ height: 18, fontSize: '0.65rem', ml: 'auto' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {project.integrations_count} integrations
                  </Typography>
                }
              />
              <Tooltip title={isProjectDefault ? "Remove as default" : "Set as default"}>
                <IconButton
                  size="small"
                  onClick={(e) => handleSetAsDefault(project, e)}
                  sx={{ ml: 1 }}
                >
                  {isProjectDefault ? (
                    <StarIcon fontSize="small" color="primary" />
                  ) : (
                    <StarBorderIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleOpenDialog}>
          <ListItemIcon>
            <AddIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Create New Project" />
        </MenuItem>

        {onSettingsClick && (
          <MenuItem onClick={() => { handleClose(); onSettingsClick(); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Project Settings" />
          </MenuItem>
        )}
      </Menu>

      {/* Create Project Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            required
            value={newProject.name}
            onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
            placeholder="e.g., Test Management System"
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Project Key"
            fullWidth
            required
            value={newProject.key}
            onChange={(e) => handleKeyChange(e.target.value)}
            placeholder="e.g., TMS"
            helperText="Uppercase letters, numbers, and underscores only"
            inputProps={{ maxLength: 10 }}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newProject.description}
            onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
            placeholder="Brief description of the project..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={creating || !newProject.name || !newProject.key}
          >
            {creating ? <CircularProgress size={20} /> : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectSelector;
